// Admin > Payments tabs: promo codes and bundles. Used codes and sold bundles can be switched off but not deleted.
import { useState, type FormEvent } from "react";
import { api, useAction, useApi } from "../../api.ts";
import { fmtMoney } from "../../format.ts";
import {
  Badge, Button, Card, Dialog, EmptyState, ErrorText, Field, IconButton, Input, Loading, LoadError, Mono, Select, Switch, Table, TBody, Td, Textarea, Th, THead, Tr, useConfirm, useToast,
} from "../../ui/index.tsx";
import { FormActions, When } from "./_ops.tsx";
import type { Bundle, Game, PromoCode } from "../../../shared/types.ts";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Toggle chips for picking games (and optionally bundles). */
function Picker({ options, value, onChange }: { options: { id: string; label: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o.id);
        return (
          <button key={o.id} type="button" onClick={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-accent/60 bg-accent-soft text-fg" : "border-line text-muted hover:border-line-strong"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Promo codes ──

const describe = (p: PromoCode) => (p.percent_off !== null ? `${p.percent_off}% off` : `${fmtMoney(p.amount_off_cents ?? 0, (p.currency ?? "gbp").toUpperCase())} off`);
const expired = (p: PromoCode) => !!p.expires_at && Date.parse(p.expires_at.replace(" ", "T") + "Z") <= Date.now();

export function PromoCodes() {
  const list = useApi<PromoCode[]>("/admin/promo-codes");
  const games = useApi<Game[]>("/games");
  const bundles = useApi<Bundle[]>("/admin/bundles");
  const toast = useToast();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [announcing, setAnnouncing] = useState<PromoCode | null>(null);
  const names = new Map<string, string>([...(games.data ?? []).map((g) => [g.id, g.name] as const), ...(bundles.data ?? []).map((b) => [`bundle:${b.id}`, `${b.name} (bundle)`] as const)]);

  const update = async (p: PromoCode, patch: Partial<Pick<PromoCode, "active" | "show_in_store">>) => {
    try { const u = await api.patch<PromoCode>(`/admin/promo-codes/${encodeURIComponent(p.code)}`, patch); list.setData((prev) => (prev ?? []).map((x) => (x.code === p.code ? u : x))); }
    catch (e) { toast(errText(e), "error"); }
  };
  const remove = async (p: PromoCode) => {
    if (!await confirm({ title: `Delete ${p.code}?`, body: "Only unused codes can be deleted.", confirmLabel: "Delete", danger: true })) return;
    try { await api.del(`/admin/promo-codes/${encodeURIComponent(p.code)}`); list.setData((prev) => (prev ?? []).filter((x) => x.code !== p.code)); }
    catch (e) { toast(errText(e), "error"); }
  };

  return (
    <>
      <Card title="Promo codes" description="Buyers enter these at checkout. Uses are counted when an order is paid." padded={false}
        actions={<Button size="sm" variant="primary" icon="plus" onClick={() => setCreating(true)}>New code</Button>}>
        {list.loading && !list.data ? <Loading /> : list.error && !list.data ? <LoadError error={list.error} retry={list.reload} /> : (list.data ?? []).length === 0 ? (
          <EmptyState icon="gift" title="No promo codes yet" body="Make one for a launch or a giveaway, then post it in Discord." />
        ) : (
          <Table>
            <THead><Tr><Th>Code</Th><Th>Discount</Th><Th>Works on</Th><Th>Used</Th><Th>Expires</Th><Th>Active</Th><Th>In store</Th><Th /></Tr></THead>
            <TBody>
              {(list.data ?? []).map((p) => (
                <Tr key={p.code}>
                  <Td><Mono className="font-semibold text-fg">{p.code}</Mono>{p.note && <div className="text-xs text-faint">{p.note}</div>}</Td>
                  <Td>{describe(p)}</Td>
                  <Td className="max-w-xs text-xs text-muted">{p.applies_to ? p.applies_to.map((x) => names.get(x) ?? x).join(", ") : "Everything"}</Td>
                  <Td><Mono>{p.uses}{p.max_uses !== null ? ` / ${p.max_uses}` : ""}</Mono></Td>
                  <Td>{p.expires_at ? <span className={expired(p) ? "text-bad" : undefined}><When at={p.expires_at} /></span> : <span className="text-faint">Never</span>}</Td>
                  <Td><Switch checked={p.active} onChange={(v) => void update(p, { active: v })} /></Td>
                  <Td><Switch checked={p.show_in_store} onChange={(v) => void update(p, { show_in_store: v })} /></Td>
                  <Td className="whitespace-nowrap text-right">
                    <IconButton icon="megaphone" label="Announce in Discord" onClick={() => setAnnouncing(p)} disabled={!p.active || expired(p) || (p.max_uses !== null && p.uses >= p.max_uses)} />
                    {p.uses === 0 && <IconButton icon="trash" label="Delete" onClick={() => void remove(p)} />}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
      <AnnounceDialog promo={announcing} onClose={() => setAnnouncing(null)} />
      <PromoForm open={creating} onClose={() => setCreating(false)} names={names}
        onSaved={(p) => { list.setData((prev) => [p, ...(prev ?? [])]); setCreating(false); toast(`${p.code} created`, "success"); }} />
    </>
  );
}

/** Posts a code to #📢-announcements. The @everyone ping is off by default and asks again before sending. */
function AnnounceDialog({ promo, onClose }: { promo: PromoCode | null; onClose: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [everyone, setEveryone] = useState(false);
  const send = useAction(async () => {
    if (!promo) return;
    if (everyone && !(await confirm({ title: "Ping @everyone?", body: "Every member of the Discord server gets a notification.", confirmLabel: "Ping everyone" }))) return;
    await api.post(`/admin/promo-codes/${encodeURIComponent(promo.code)}/announce`, { ping_everyone: everyone });
    toast(`${promo.code} posted to #📢-announcements`, "success");
    setEveryone(false);
    onClose();
  });
  return (
    <Dialog open={!!promo} onClose={onClose} title={`Announce ${promo?.code ?? ""} in Discord`}>
      {promo && (
        <div className="space-y-4">
          <p className="text-sm text-muted">Posts "{describe(promo)} with code {promo.code}" to #📢-announcements, with the expiry and remaining uses, and Shop now buttons.</p>
          <Switch checked={everyone} onChange={setEveryone} label={<span className="text-sm">Ping <b>@everyone</b></span>} />
          <ErrorText>{send.error}</ErrorText>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" icon="send" loading={send.pending} onClick={() => void send.run()}>Post</Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function PromoForm({ open, onClose, onSaved, names }: { open: boolean; onClose: () => void; onSaved: (p: PromoCode) => void; names: Map<string, string> }) {
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [value, setValue] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expires, setExpires] = useState("");
  const [scope, setScope] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [inStore, setInStore] = useState(false);
  const save = useAction(async () => {
    const n = Number(value);
    const body = {
      code: code.trim(), note: note.trim() || undefined,
      ...(kind === "percent" ? { percent_off: Math.round(n) } : { amount_off_cents: Math.round(n * 100), currency: "gbp" }),
      max_uses: maxUses ? Number(maxUses) : undefined,
      expires_at: expires ? new Date(expires).toISOString() : undefined,
      applies_to: scope.length ? scope : undefined,
      show_in_store: inStore,
    };
    onSaved(await api.post<PromoCode>("/admin/promo-codes", body));
    setCode(""); setValue(""); setMaxUses(""); setExpires(""); setScope([]); setNote(""); setInStore(false);
  });
  const submit = (e: FormEvent) => { e.preventDefault(); void save.run(); };
  return (
    <Dialog open={open} onClose={onClose} title="New promo code">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Code" hint="Letters, numbers, - and _. Not case-sensitive for buyers.">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="LAUNCH20" className="font-mono" required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select value={kind} onChange={(e) => setKind(e.target.value as "percent" | "amount")}><option value="percent">Percent off</option><option value="amount">Fixed £ off</option></Select>
          </Field>
          <Field label={kind === "percent" ? "Percent (1-90)" : "Amount (£)"}>
            <Input type="number" min={kind === "percent" ? 1 : 0.01} max={kind === "percent" ? 90 : undefined} step={kind === "percent" ? 1 : 0.01} value={value} onChange={(e) => setValue(e.target.value)} required />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Max uses" hint="Empty = unlimited"><Input type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} /></Field>
          <Field label="Expires" hint="Empty = never"><Input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} /></Field>
        </div>
        <Field label="Works on" hint="Nothing picked = every game and bundle">
          <Picker options={[...names].map(([id, label]) => ({ id, label }))} value={scope} onChange={setScope} />
        </Field>
        <Switch checked={inStore} onChange={setInStore} label={<span className="text-sm">Show publicly <span className="text-faint">(Pricing banner and the Discord storefront)</span></span>} />
        <Field label="Note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reopening launch, posted in #announcements" /></Field>
        <ErrorText>{save.error}</ErrorText>
        <FormActions onCancel={onClose} submitLabel="Create code" loading={save.pending} disabled={!code.trim() || !value} />
      </form>
    </Dialog>
  );
}

// ── Bundles ──

export function Bundles() {
  const list = useApi<Bundle[]>("/admin/bundles");
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Bundle | "new" | null>(null);

  const toggle = async (b: Bundle, active: boolean) => {
    try { const u = await api.patch<Bundle>(`/admin/bundles/${b.id}`, { active }); list.setData((prev) => (prev ?? []).map((x) => (x.id === b.id ? u : x))); }
    catch (e) { toast(errText(e), "error"); }
  };
  const remove = async (b: Bundle) => {
    if (!await confirm({ title: `Delete ${b.name}?`, body: "Bundles that have been sold can only be switched off.", confirmLabel: "Delete", danger: true })) return;
    try { await api.del(`/admin/bundles/${b.id}`); list.setData((prev) => (prev ?? []).filter((x) => x.id !== b.id)); }
    catch (e) { toast(errText(e), "error"); }
  };

  return (
    <>
      <Card title="Bundles" description="Several games, one duration, one price. Shown on Pricing while every game in them is on sale." padded={false}
        actions={<Button size="sm" variant="primary" icon="plus" onClick={() => setEditing("new")}>New bundle</Button>}>
        {list.loading && !list.data ? <Loading /> : list.error && !list.data ? <LoadError error={list.error} retry={list.reload} /> : (list.data ?? []).length === 0 ? (
          <EmptyState icon="package" title="No bundles yet" body="For example both Minecraft editions for 30 days at a little under the two separately." />
        ) : (
          <Table>
            <THead><Tr><Th>Bundle</Th><Th>Games</Th><Th>Duration</Th><Th>Price</Th><Th>On sale</Th><Th>Active</Th><Th /></Tr></THead>
            <TBody>
              {(list.data ?? []).map((b) => (
                <Tr key={b.id}>
                  <Td><div className="font-medium">{b.name}</div><Mono className="text-xs text-faint">{b.id}</Mono></Td>
                  <Td className="max-w-xs text-xs text-muted">{b.game_names.join(", ")}</Td>
                  <Td>{b.days === null ? "Lifetime" : `${b.days} days`}</Td>
                  <Td><Mono>{b.price}</Mono></Td>
                  <Td>{b.purchasable ? <Badge tone="ok" dot>Yes</Badge> : <Badge tone="warn">{b.active ? "A game is off sale" : "Off"}</Badge>}</Td>
                  <Td><Switch checked={b.active} onChange={(v) => void toggle(b, v)} /></Td>
                  <Td className="whitespace-nowrap text-right">
                    <IconButton icon="edit" label="Edit" onClick={() => setEditing(b)} />
                    <IconButton icon="trash" label="Delete" onClick={() => void remove(b)} />
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
      {editing && <BundleForm bundle={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(b) => { list.setData((prev) => (editing === "new" ? [...(prev ?? []), b] : (prev ?? []).map((x) => (x.id === b.id ? b : x)))); setEditing(null); toast("Bundle saved", "success"); }} />}
    </>
  );
}

function BundleForm({ bundle, onClose, onSaved }: { bundle: Bundle | null; onClose: () => void; onSaved: (b: Bundle) => void }) {
  const games = useApi<Game[]>("/games");
  const [id, setId] = useState(bundle?.id ?? "");
  const [name, setName] = useState(bundle?.name ?? "");
  const [description, setDescription] = useState(bundle?.description ?? "");
  const [picked, setPicked] = useState<string[]>(bundle?.games ?? []);
  const [lifetime, setLifetime] = useState(bundle ? bundle.days === null : false);
  const [days, setDays] = useState(String(bundle?.days ?? 30));
  const [price, setPrice] = useState(bundle?.price ?? "");
  const save = useAction(async () => {
    const body = { name: name.trim(), description: description.trim(), games: picked, days: lifetime ? null : Number(days), price: price.trim() };
    onSaved(bundle ? await api.patch<Bundle>(`/admin/bundles/${bundle.id}`, body) : await api.post<Bundle>("/admin/bundles", { id: id.trim(), ...body }));
  });
  const submit = (e: FormEvent) => { e.preventDefault(); void save.run(); };
  const options = (games.data ?? []).filter((g) => g.status !== "discontinued").map((g) => ({ id: g.id, label: g.name }));
  return (
    <Dialog open onClose={onClose} title={bundle ? `Edit ${bundle.name}` : "New bundle"}>
      <form onSubmit={submit} className="space-y-4">
        {!bundle && <Field label="ID" hint="Lowercase, used in the checkout link, e.g. minecraft-both"><Input value={id} onChange={(e) => setId(e.target.value.toLowerCase())} className="font-mono" required /></Field>}
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Minecraft: Both Editions" required /></Field>
        <Field label="Description (optional)"><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label="Games" hint="At least two"><Picker options={options} value={picked} onChange={setPicked} /></Field>
        <div className="grid grid-cols-2 items-end gap-3">
          <Field label="Duration (days)"><Input type="number" min={1} max={3650} value={days} disabled={lifetime} onChange={(e) => setDays(e.target.value)} /></Field>
          <div className="pb-2"><Switch checked={lifetime} onChange={setLifetime} label="Lifetime" /></div>
        </div>
        <Field label="Price" hint="Like £12 or £24.99"><Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="£12" required /></Field>
        <ErrorText>{save.error}</ErrorText>
        <FormActions onCancel={onClose} submitLabel={bundle ? "Save" : "Create bundle"} loading={save.pending} disabled={picked.length < 2 || !name.trim() || !price.trim()} />
      </form>
    </Dialog>
  );
}
